/**
 * THE CONSENT GATE — what stands between `/play` and the game.
 *
 * ── THE HOLE THIS FILLS ─────────────────────────────────────────────────────
 * Publishing a new version of the terms supersedes every existing acceptance.
 * That is the documented, deliberate effect of `POST /api/admin/legal-versions`,
 * and the admin panel puts a number on it before the operator presses the
 * button: "this will require N players to accept again".
 *
 * Nothing made it true. `ConsentService.getRequirements` — whose own comment
 * reads "what the user must still accept before they are allowed to play" — had
 * exactly one caller, the account settings screen, which merely DISPLAYS the
 * state. No screen and no server path ever asked the question on the way into a
 * match, so a republished document changed nothing for anybody: every player
 * carried on playing under terms they were on record as never having seen.
 * The `terms.reaccept.*` strings shipped in all eight catalogues, unused.
 *
 * This module is the client half. `backend/src/game/match/auth.ts` step (8) is
 * the other half and is the one that cannot be skipped — the gate below is what
 * makes the refusal comprehensible instead of an opaque 403 on a websocket
 * upgrade.
 *
 * ── WHY IT FAILS CLOSED ─────────────────────────────────────────────────────
 * If `GET /api/consent/required` cannot be reached, this shows an error with a
 * retry rather than waving the player through. Waving them through does not
 * make them able to play — the socket gate refuses the upgrade anyway — it just
 * moves the failure somewhere it cannot be explained. There is no offline mode
 * to protect: every screen behind this needs the same backend.
 *
 * ── WHY THE TEXT IS FETCHED AGAIN, NOT TAKEN FROM THE STATE REPORT ──────────
 * `GET /consent/required` returns a `LegalDocumentRef` — metadata, no body.
 * A re-acceptance screen that showed a version NUMBER and asked for agreement
 * would be asking someone to agree to a string of digits. The frozen snapshot
 * from `GET /consent/documents/:purpose` is fetched and rendered in full, and
 * it is the same bytes whose hash the acceptance is recorded against.
 */
import {
  httpAccountApi,
  type AccountApi,
  type ConsentPurpose,
  type ConsentRequirementReport,
  type ConsentStateItem,
} from '@/engine/account/account-api';
import {
  fetchRequiredDocuments,
  type LegalDocumentSnapshot,
  type RequiredPurpose,
} from '@/engine/auth/register-api';
import { formatDate, t, tError } from '@/engine/i18n';
import { TAP_TARGET, el, replaceChildren, type Child } from '@/engine/layout/dom';
import { ROUTES } from '@/engine/layout/routes';
import { buttonClass } from '@/engine/ui/button';
import { createSlotIcon } from '@/engine/ui/icon-registry';
import { attachPress } from '@/engine/ui/press';
import { applyProseClasses, renderRichText } from '@/engine/pages/content/rich-text';

/** The two calls the gate makes. Narrowed from `AccountApi` so tests are cheap. */
export interface ConsentGateApi {
  consentState: AccountApi['consentState'];
  grantConsent: AccountApi['grantConsent'];
  /** The frozen bodies of the required documents. Defaults to the real fetch. */
  documents?: () => Promise<Readonly<Record<RequiredPurpose, LegalDocumentSnapshot | null>>>;
}

export interface ConsentGateOptions {
  /**
   * Everything is accepted — mount what was behind the gate. Its teardown, if
   * it returns one, is called by the gate's own teardown.
   */
  readonly onSatisfied: () => (() => void) | void;
  /** The player chose "not now". Normally routes back to the landing page. */
  readonly onDecline: () => void;
  readonly api?: ConsentGateApi;
}

type Phase = 'checking' | 'passed' | 'blocked' | 'unpublished' | 'declined' | 'failed';

const PANEL =
  'mx-auto w-full max-w-[46rem] px-4 py-8 ' +
  'ps-[max(1rem,env(safe-area-inset-left))] pe-[max(1rem,env(safe-area-inset-right))] ' +
  'pb-[max(2rem,env(safe-area-inset-bottom))]';
/** The shared AAA skin (gold CTA / slate plate) + layout. TAP_TARGET keeps the 44 px floor. */
const PRIMARY = buttonClass('primary', 'md', `${TAP_TARGET} gap-2 px-5 font-bold`);
const GHOST = buttonClass('secondary', 'md', `${TAP_TARGET} gap-2 px-5 no-underline`);

/**
 * Check the player's consent, then either mount what is behind the gate or ask
 * them to accept the document that is now in force.
 *
 * Returns a teardown that also tears down whatever `onSatisfied` mounted, so
 * the router keeps one handle per route and not two.
 */
export function mountConsentGate(root: HTMLElement, options: ConsentGateOptions): () => void {
  const api: ConsentGateApi = options.api ?? httpAccountApi;
  const loadDocuments = api.documents ?? (() => fetchRequiredDocuments());

  let disposed = false;
  let phase: Phase = 'checking';
  let report: ConsentRequirementReport | null = null;
  let bodies: Readonly<Record<RequiredPurpose, LegalDocumentSnapshot | null>> | null = null;
  let error: unknown = null;
  /** Set while an acceptance is in flight, so the button cannot be double-fired. */
  let submitting = false;
  /** The teardown of whatever `onSatisfied` mounted. */
  let inner: (() => void) | null = null;

  const render = (): void => {
    if (disposed || phase === 'passed') return;
    replaceChildren(root, view());
  };

  const pass = (): void => {
    if (disposed) return;
    phase = 'passed';
    replaceChildren(root);
    inner = options.onSatisfied() ?? null;
  };

  const check = (): void => {
    phase = 'checking';
    error = null;
    render();
    void api
      .consentState()
      .then(async (state) => {
        if (disposed) return;
        report = state;
        if (!state.blocked) {
          pass();
          return;
        }
        // Only NOW is the text worth fetching: the overwhelmingly common case
        // is a player whose consent is current, and they should not pay for two
        // legal documents they are not going to be shown.
        if (state.unpublished.length > 0) {
          phase = 'unpublished';
          render();
          return;
        }
        bodies = await loadDocuments();
        if (disposed) return;
        phase = 'blocked';
        render();
      })
      .catch((err: unknown) => {
        if (disposed) return;
        error = err;
        phase = 'failed';
        render();
      });
  };

  const accept = (item: ConsentStateItem): void => {
    if (submitting || item.document === null) return;
    submitting = true;
    error = null;
    render();
    void api
      .grantConsent(item.purpose, item.document)
      .then(() => {
        if (disposed) return;
        submitting = false;
        // Re-ask rather than assume: a second required document may still be
        // outstanding, and a publish can land between the two acceptances.
        check();
      })
      .catch((err: unknown) => {
        if (disposed) return;
        submitting = false;
        error = err;
        // A 409 CONSENT_DOCUMENT_SUPERSEDED means the text moved under us. The
        // only correct response is to re-read and re-prompt with the NEW text —
        // never to retry the same version, which would record agreement to
        // something that is no longer in force.
        if (isSupersededError(err)) {
          check();
          return;
        }
        render();
      });
  };

  /* ──────────────────────────────────────────────────────────── the views ── */

  function view(): HTMLElement {
    return el('main', { id: 'main', tabindex: '-1', class: PANEL }, bodyOf());
  }

  function bodyOf(): readonly Child[] {
    switch (phase) {
      case 'failed':
        return failedView();
      case 'unpublished':
        return unpublishedView();
      case 'declined':
        return declinedView();
      case 'blocked':
        return blockedView();
      default:
        return [
          el('p', {
            class: 'py-8 text-text-dim',
            role: 'status',
            'aria-live': 'polite',
            text: t('common.loading'),
          }),
        ];
    }
  }

  function failedView(): Child[] {
    const retry = el('button', { type: 'button', class: PRIMARY, 'data-sfx': 'confirm' }, [
      createSlotIcon(document, 'action-retry', { size: 20, class: 'rga-btn__icon' }),
      t('common.retry'),
    ]);
    attachPress(retry, { onPress: () => check() });
    return [
      el('h1', { class: 'mb-2 font-heading text-2xl text-gold', text: t('errors.generic') }),
      el('p', { class: 'mb-6 text-error', role: 'alert', text: tError(error) }),
      el('div', { class: 'flex flex-wrap gap-3' }, [retry, homeLink()]),
    ];
  }

  /**
   * NOBODY HAS PUBLISHED THE TERMS. That is an operator error and the copy says
   * so — a player told "you must accept our terms" with nothing to accept would
   * reasonably conclude the fault is theirs. `terms.documents_unavailable` is
   * the catalogue's sentence for exactly this.
   */
  function unpublishedView(): Child[] {
    return [
      el('h1', {
        class: 'mb-2 font-heading text-2xl text-gold',
        text: t('terms.reaccept.title'),
      }),
      el('p', { class: 'mb-6 text-text-dim', role: 'alert', text: t('terms.documents_unavailable') }),
      el('div', { class: 'flex flex-wrap gap-3' }, [homeLink()]),
    ];
  }

  function declinedView(): Child[] {
    return [
      el('h1', {
        class: 'mb-2 font-heading text-2xl text-gold',
        text: t('terms.reaccept.declined_title'),
      }),
      el('p', { class: 'mb-6 text-text-dim', text: t('terms.reaccept.declined_body') }),
      el('div', { class: 'flex flex-wrap gap-3' }, [homeLink()]),
    ];
  }

  /**
   * The document that changed, in full, with one accept action.
   *
   * ONE document at a time, deliberately: each required purpose is its own
   * consent with its own version and its own row, and stacking two full legal
   * texts under a single button is the bundling that makes a consent invalid.
   * Accepting the first re-checks and, if the privacy policy also moved, asks
   * again with that text.
   */
  function blockedView(): Child[] {
    const items = (report?.items ?? []).filter((i) => i.required && !i.satisfied);
    const item = items[0];
    if (item === undefined || item.document === null) return unpublishedView();

    const snapshot = bodies?.[item.purpose as RequiredPurpose] ?? null;
    const doc = item.document;

    const acceptButton = el('button', {
      type: 'button',
      class: PRIMARY,
      'data-testid': 'reaccept-accept',
      'aria-busy': submitting ? 'true' : false,
      'data-sfx': 'confirm',
      // Visibly busy too (it used to be aria-busy only): disabled + spinner.
      ...(submitting ? { disabled: true } : {}),
    }, [
      createSlotIcon(document, 'action-confirm', { size: 20, class: 'rga-btn__icon' }),
      t('terms.reaccept.accept'),
      submitting ? el('span', { class: 'rga-btn__spinner', 'aria-hidden': 'true' }) : null,
    ]);
    if (submitting) acceptButton.classList.add('is-loading');
    attachPress(acceptButton, { onPress: () => accept(item) });

    const declineButton = el('button', {
      type: 'button',
      class: GHOST,
      text: t('terms.reaccept.decline'),
    });
    attachPress(declineButton, { onPress: () => {
      phase = 'declined';
      render();
      options.onDecline();
    } });

    const children: Child[] = [
      el('h1', {
        class: 'mb-2 font-heading text-2xl text-gold',
        text: t('terms.reaccept.title'),
      }),
      el('p', {
        class: 'mb-1 text-text',
        text: t('terms.reaccept.body', { version: doc.version, document: doc.title }),
      }),
      el('p', {
        class: 'mb-6 text-sm text-text-dim',
        text: t('legal.effective_from', { date: formatDate(new Date(doc.effectiveFrom)) }),
      }),
    ];

    if (snapshot !== null) {
      // A scroll box, not a link away: the acceptance is recorded against these
      // exact bytes, so these exact bytes are what is put in front of the player.
      const article = el('article', {
        class: 'prose-legal max-h-[50vh] overflow-y-auto rounded border border-border bg-surface/40 p-4',
        tabindex: '0',
        role: 'region',
        'aria-label': doc.title,
      });
      renderRichText(article, snapshot.content, { externalLinkLabel: t('a11y.external_link') });
      applyProseClasses(article);
      children.push(article);
      children.push(
        el('p', {
          class: 'mt-2 text-xs font-mono break-all text-text-dim',
          text: `sha256 ${doc.contentHash}`,
        }),
      );
    } else {
      // The list said a version is in force but its body would not load. Offer
      // the page instead of an accept button: agreeing to text we could not
      // show is the one outcome this whole module exists to prevent.
      children.push(
        el('p', { class: 'text-text-dim', role: 'alert', text: t('errors.generic') }),
        el('p', {}, [
          el('a', {
            class: `${TAP_TARGET} text-gold underline`,
            href: `${ROUTES.legal}/${doc.slug}`,
            text: t('terms.reaccept.view_changes'),
          }),
        ]),
      );
    }

    if (error !== null) {
      children.push(el('p', { class: 'mt-4 text-error', role: 'alert', text: tError(error) }));
    }

    children.push(
      el('p', { class: 'mt-4 text-sm text-text-dim', text: t('terms.recorded_note') }),
      el('div', { class: 'mt-4 flex flex-wrap gap-3' }, [
        snapshot === null ? null : acceptButton,
        declineButton,
      ]),
    );
    return children;
  }

  function homeLink(): HTMLElement {
    const home = el('a', { class: GHOST, href: ROUTES.home }, [
      createSlotIcon(document, 'action-home', { size: 20, class: 'rga-btn__icon' }),
      t('nav.home'),
    ]);
    attachPress(home, { onPress: () => undefined });
    return home;
  }

  check();

  return () => {
    disposed = true;
    inner?.();
    inner = null;
    replaceChildren(root);
  };
}

/**
 * Did the server say the document moved while this screen was open?
 *
 * `ApiError` is not imported: the gate takes its api as an interface so a test
 * can reject with a plain object, and a duck-typed check works for both. The
 * code is the contract (`CONSENT_DOCUMENT_SUPERSEDED`); the 409 alone is not,
 * because other conflicts share that status.
 */
export function isSupersededError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const e = err as { status?: unknown; details?: unknown; code?: unknown };
  if (e.code === 'CONSENT_DOCUMENT_SUPERSEDED') return true;
  const details = e.details as { code?: unknown; error?: unknown } | undefined;
  if (details?.code === 'CONSENT_DOCUMENT_SUPERSEDED') return true;
  if (details?.error === 'CONSENT_DOCUMENT_SUPERSEDED') return true;
  return false;
}

export type { ConsentPurpose };
